import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { DrawerBodyWrapper, DrawerFooterWrapper, MultiSelect, TOAST_TYPE } from '@base'
import { Button, Calendar, Input, Select } from '@baseV2'
import {
	INDIAN_STATES,
	strings,
	TENDERBOOK_ALL_STATES_OPTION,
	TENDERBOOK_DURATION_OPTIONS,
	TENDERBOOK_PLAN_NAMES,
	TENDERBOOK_PLAN_OPTIONS,
	TENDERBOOK_SUBSCRIPTION_TYPES,
} from '@constants'
import { createTenderUserSubscription } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { Spacings } from '@styles'

const Field = styled.div`
	margin-bottom: ${Spacings.SPACING_4B};
`

const DateRange = styled.div`
	display: flex;
	gap: ${Spacings.SPACING_3B};

	& > * {
		flex: 1;
	}
`

const DAY_IN_MS = 24 * 60 * 60 * 1000

const MAX_TRIAL_DAYS = 7

const startOfToday = () => {
	const d = new Date()
	d.setHours(0, 0, 0, 0)
	return d
}

const UpdateSubscription = ({ data, update }) => {
	const dispatch = useDispatch()

	const [plan, setPlan] = useState({})
	const [duration, setDuration] = useState({})
	const [states, setStates] = useState([])
	const [fromDate, setFromDate] = useState()
	const [toDate, setToDate] = useState()

	const [companyName, setCompanyName] = useState(data?.company_name ?? '')
	const [phone, setPhone] = useState(data?.mobile_no ?? '')
	const [gst, setGst] = useState(data?.gst ?? '')
	const [address, setAddress] = useState(data?.company_profile?.address ?? '')
	const [amount, setAmount] = useState('')

	const isStandard = plan?.id === TENDERBOOK_PLAN_NAMES.STANDARD

	const isTrial = plan?.id === TENDERBOOK_PLAN_NAMES.TRIAL

	const toMinDate = fromDate ? new Date(fromDate) : startOfToday()
	const toMaxDate = fromDate ? new Date(fromDate + MAX_TRIAL_DAYS * DAY_IN_MS) : undefined

	const canSubmit = useMemo(() => {
		if (!plan?.id) return false

		if (!amount) return false

		if (isTrial) {
			if (!fromDate || !toDate) return false
		} else if (!duration?.id) {
			return false
		}

		if (!isStandard && !isTrial && states.length === 0) return false
		return true
	}, [plan, duration, isStandard, isTrial, states, fromDate, toDate, amount])

	const onPlanChange = (option) => {
		setPlan(option)
		// Reset dependent fields when switching plans.
		setStates([])
		setFromDate(undefined)
		setToDate(undefined)
	}

	const onFromDateChange = (ts) => {
		setFromDate(ts)
		// Clear To-date if it now falls outside the valid [from, from + 7d] window.
		if (toDate && (toDate < ts || toDate > ts + MAX_TRIAL_DAYS * DAY_IN_MS)) {
			setToDate(undefined)
		}
	}

	const submitHandler = () => {
		const payload = {
			user_id: data?.user_id,
			mobile_no: phone,
			billing_details: {
				company_name: companyName,
				billing_address: address,
				gst_number: gst,
			},
			plan_name: plan?.id,
			subscription_status: isTrial ? TENDERBOOK_SUBSCRIPTION_TYPES.TRIAL : TENDERBOOK_SUBSCRIPTION_TYPES.SUBSCRIBED,
			amount,
			regions: isStandard ? [TENDERBOOK_ALL_STATES_OPTION.id] : states.map((s) => s.label),
			...(isTrial ? { start_date: fromDate, end_date: toDate } : { subscription_duration_in_months: duration?.id }),
		}

		dispatch(showLoader(true))
		createTenderUserSubscription(payload)
			.then(() => {
				dispatch(showToast(true, 'Subscription updated', { type: TOAST_TYPE.SUCCESS }))
				update?.()
				dispatch(toggleDrawer(false))
			})
			.catch((error) => {
				dispatch(showToast(true, error?.response?.data?.message ?? 'Failed to update subscription', { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	return (
		<>
			<DrawerBodyWrapper>
				<Field>
					<Input
						id='company_name'
						label='Company name'
						placeholder='Company name'
						value={companyName}
						onChange={(e) => setCompanyName(e.target.value)}
						disabled
					/>
				</Field>

				<Field>
					<Input
						id='phone'
						label='Phone number'
						placeholder='Phone number'
						type='tel'
						value={phone}
						onChange={(e) => setPhone(e.target.value)}
						disabled
					/>
				</Field>

				<Field>
					<Input id='gst' label='GST (optional)' placeholder='GST' value={gst} onChange={(e) => setGst(e.target.value.toUpperCase())} />
				</Field>

				<Field>
					<Input
						id='address'
						label='Bidder address (optional)'
						placeholder='Bidder address'
						value={address}
						onChange={(e) => setAddress(e.target.value)}
					/>
				</Field>

				<Field>
					<Select
						id='plan_name'
						label='Plan name'
						placeholder='Select plan'
						data={TENDERBOOK_PLAN_OPTIONS}
						primaryKey='id'
						displayKey='label'
						value={plan}
						onChange={onPlanChange}
					/>
				</Field>

				{isTrial ? (
					<Field>
						<DateRange>
							<Calendar label='From' placeholder='Start date' value={fromDate} minDate={startOfToday()} onChange={onFromDateChange} />
							<Calendar
								label='To'
								placeholder='End date'
								value={toDate}
								minDate={toMinDate}
								maxDate={toMaxDate}
								disabled={!fromDate}
								onChange={setToDate}
							/>
						</DateRange>
					</Field>
				) : (
					<Field>
						<Select
							id='duration'
							label='Duration (in months)'
							placeholder='Select duration'
							data={TENDERBOOK_DURATION_OPTIONS}
							primaryKey='id'
							displayKey='label'
							value={duration}
							onChange={setDuration}
						/>
					</Field>
				)}

				{!isTrial && (
					<Field>
						{isStandard ? (
							<Select
								id='states_all'
								label='States'
								data={[]}
								primaryKey='id'
								displayKey='label'
								value={TENDERBOOK_ALL_STATES_OPTION}
								disabled
							/>
						) : (
							<MultiSelect
								id='states'
								label='States'
								placeholder='Select states'
								data={INDIAN_STATES}
								primaryKey='id'
								displayKey='label'
								value={states}
								onChange={setStates}
								showAllOptions
							/>
						)}
					</Field>
				)}

				<Field>
					<Input
						id='amount'
						label='Amount'
						placeholder='Amount'
						type='number'
						min={0}
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						required
					/>
				</Field>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button onClick={submitHandler} disabled={!canSubmit}>
					{strings('submit')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default UpdateSubscription
